import React, { createContext, useState, useCallback } from 'react';

/**
 * Контекст для управления данными текущего пользователя
 * Предоставляет доступ к профилю пользователя и функциям для его обновления
 */
export const UserContext = createContext(null);

/**
 * Провайдер контекста пользователя
 * Должен обертывать все компоненты приложения, которым нужны данные пользователя
 */
export const UserProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [userError, setUserError] = useState(null);

  /**
   * Устанавливает данные текущего пользователя
   * @param {Object} userData - Объект с данными пользователя (id, email, login, avatar, bio, ...)
   */
  const setUser = useCallback((userData) => {
    setCurrentUser(userData);
    setUserError(null);
  }, []);

  /**
   * Очищает данные пользователя (используется при logout)
   */
  const clearUser = useCallback(() => {
    setCurrentUser(null);
    setUserError(null);
  }, []);

  /**
   * Устанавливает состояние загрузки
   * @param {boolean} isLoading
   */
  const setUserLoading = useCallback((isLoading) => {
    setIsLoadingUser(isLoading);
  }, []);

  /**
   * Устанавливает ошибку загрузки
   * @param {string|null} error
   */
  const setUserErrorState = useCallback((error) => {
    setUserError(error);
  }, []);

  /**
   * Обновляет отдельное поле пользователя
   * @param {string} field - Имя поля для обновления
   * @param {any} value - Новое значение
   */
  const updateUserField = useCallback((field, value) => {
    setCurrentUser((prev) => {
      if (!prev) return prev;
      return { ...prev, [field]: value };
    });
  }, []);

  const value = {
    currentUser,
    isLoadingUser,
    userError,
    setUser,
    clearUser,
    setUserLoading,
    setUserErrorState,
    updateUserField,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * Хук для использования UserContext
 * Выбросит ошибку если использован вне провайдера
 */
export const useUser = () => {
  const context = React.useContext(UserContext);
  if (context === null) {
    throw new Error('useUser должен использоваться внутри UserProvider');
  }
  return context;
};
